import { Controller } from "@hotwired/stimulus"
import type { createElement as CreateElementFn } from "react"
import type { Root } from "react-dom/client"
import type { GalleryLightbox as GalleryLightboxComponent } from "@/components/GalleryLightbox"

export default class extends Controller {
  static values = { images: Array }
  declare imagesValue: { src: string; caption?: string }[]

  private root: Root | null = null
  private mountEl: HTMLDivElement | null = null
  private createElementFn: typeof CreateElementFn | null = null
  private galleryLightbox: typeof GalleryLightboxComponent | null = null

  disconnect() {
    this.close()
  }

  select(event: Event) {
    event.preventDefault()
    const trigger = event.currentTarget as HTMLElement
    void this.open(Number(trigger.dataset.galleryLightboxIndex))
  }

  private async ensureLoaded() {
    if (this.createElementFn && this.galleryLightbox && this.root) return

    const [{ createElement }, { createRoot }, { GalleryLightbox }] =
      await Promise.all([
        import("react"),
        import("react-dom/client"),
        import("@/components/GalleryLightbox"),
      ])

    this.createElementFn = createElement
    this.galleryLightbox = GalleryLightbox

    if (!this.root) {
      this.mountEl = document.createElement("div")
      document.body.appendChild(this.mountEl)
      this.root = createRoot(this.mountEl)
    }
  }

  private async open(index: number) {
    await this.ensureLoaded()
    this.renderAt(index)
  }

  private renderAt(index: number) {
    if (!this.root || !this.createElementFn || !this.galleryLightbox) return

    this.root.render(
      this.createElementFn(this.galleryLightbox, {
        slides: this.imagesValue.map((image) => ({
          src: image.src,
          description: image.caption,
        })),
        index,
        open: true,
        onIndexChange: (i: number) => this.renderAt(i),
        onClose: () => this.close(),
      }),
    )
  }

  private close() {
    this.root?.unmount()
    this.root = null
    this.mountEl?.remove()
    this.mountEl = null
  }
}
